"use client";

import { useState, useEffect, useCallback } from "react";
import { useQuery, useAction } from "convex/react";
import { Link } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CancellationConfirmationModal } from "@/components/billing/CancellationConfirmationModal";
import { usePageTitle } from "@/lib/usePageTitle";
import { useSubscription } from "@/hooks/useSubscription";
import { useCheckout } from "@/hooks/useCheckout";
import {
  PRICING_PLANS,
  TRIAL_DAYS,
  type PricingPlanId,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function tierLabel(tier: SubscriptionTier) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

function formatDate(ts: number | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function daysUntil(ts: number): number {
  return Math.max(0, Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24)));
}

function formatCurrency(amountCents: number, currency: string) {
  const amount = amountCents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const PLAN_ORDER = ["starter", "plus", "pro"];

function eventLabel(eventType: string): string {
  switch (eventType) {
    case "subscription.created": return "Subscription created";
    case "subscription.updated": return "Subscription updated";
    case "subscription.canceled": return "Subscription canceled";
    case "subscription.expired": return "Subscription expired";
    case "checkout.created": return "Checkout initiated";
    case "checkout.updated": return "Checkout updated";
    default: return eventType.replace(/\./g, " ").replace(/^\w/, (c) => c.toUpperCase());
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({
  status,
  cancelAtPeriodEnd,
}: {
  status: SubscriptionStatus;
  cancelAtPeriodEnd?: boolean;
}) {
  if (cancelAtPeriodEnd) {
    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Cancellation Scheduled
      </Badge>
    );
  }
  if (status === "active") {
    return <Badge variant="success">Active</Badge>;
  }
  if (status === "trialing") {
    return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Trial</Badge>;
  }
  if (status === "past_due") {
    return <Badge variant="destructive">Past Due</Badge>;
  }
  if (status === "canceled") {
    return <Badge variant="secondary">Canceled</Badge>;
  }
  return null;
}

function InvoiceStatusBadge({ status }: { status: string }) {
  if (status === "paid" || status === "succeeded" || status === "completed") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
        Paid
      </span>
    );
  }
  if (status === "pending" || status === "open") {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Pending
      </span>
    );
  }
  if (status === "refunded") {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        Refunded
      </span>
    );
  }
  if (status === "failed" || status === "disputed") {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const isNearLimit = pct >= 80;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{used} of {limit} used</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
        <div
          className={`h-full rounded-full transition-all ${isNearLimit ? "bg-amber-500" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function PlanFeatures({ tier }: { tier: SubscriptionTier }) {
  const plan = PRICING_PLANS.find((p) => p.id === tier);
  if (!plan) return null;
  return (
    <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckIcon />
          {f}
        </li>
      ))}
    </ul>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AccountSubscriptionPage() {
  usePageTitle("Subscription");

  const sub = useSubscription();
  const summary = useQuery(api.billing.getMySummary);
  const history = useQuery(api.billing.getMySubscriptionHistory, { limit: 20 });
  const openPortal = useAction(api.actions.openCustomerPortal);
  const scheduleCancellation = useAction(api.actions.scheduleSubscriptionCancellation);
  const resumeSub = useAction(api.actions.resumeSubscription);
  const fetchInvoices = useAction(api.actions.getMyInvoices);
  const { startCheckout, loadingPlan, error: checkoutError } = useCheckout();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Invoices fetched from Polar API
  const [invoices, setInvoices] = useState<Array<{
    id: string;
    createdAt: string;
    amount: number;
    currency: string;
    taxAmount: number;
    status: string;
    productName: string | null;
    billingReason: string | null;
    invoiceUrl: string | null;
  }> | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const result = await fetchInvoices({ limit: 20 });
      if (result.ok && result.data) {
        setInvoices(result.data.invoices);
      } else {
        setInvoices([]);
      }
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [fetchInvoices]);

  // Fetch invoices once when the user has a subscription
  useEffect(() => {
    if (!sub.isLoading && sub.tier !== "free" && sub.status !== "none") {
      loadInvoices();
    }
  }, [sub.isLoading, sub.tier, sub.status, loadInvoices]);

  // Plan change confirmation
  const [pendingPlanChange, setPendingPlanChange] = useState<{
    planId: PricingPlanId;
    isUpgrade: boolean;
    isDowngrade: boolean;
  } | null>(null);

  if (sub.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <Skeleton className="h-7 w-48" />
          <Skeleton className="mt-1 h-4 w-72" />
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  const tier = sub.tier;
  const status = sub.status;
  const hasSubscription = status !== "none" && tier !== "free";
  const isActive = sub.hasAccess && (status === "active" || status === "trialing");
  const isCancelScheduled = sub.cancelAtPeriodEnd;
  const billingDate = formatDate(sub.currentPeriodEnd);
  const serverLimit = sub.serverLimit;

  // ─── Handlers ─────────────────────────────────────────────────────────

  async function handleConfirmCancel() {
    setShowCancelModal(false);
    setCancelling(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await scheduleCancellation();
      if (!result.ok) {
        setError(result.error?.message ?? "Failed to schedule cancellation.");
        setCancelling(false);
        return;
      }
      setSuccessMessage("Cancellation scheduled. Your access continues until the end of your billing period.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setCancelling(false);
    }
  }

  async function handleOpenPortal() {
    setError(null);
    setSuccessMessage(null);
    setPortalLoading(true);
    try {
      const result = await openPortal({});
      if (!result.ok) throw new Error(result.error?.message || "Unable to open portal.");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to open customer portal.");
    } finally {
      setPortalLoading(false);
    }
  }

  async function handleResume() {
    setError(null);
    setSuccessMessage(null);
    setResumeLoading(true);
    try {
      const result = await resumeSub();
      if (!result.ok) throw new Error(result.error?.message || "Resume failed.");
      setSuccessMessage("Auto-renew has been re-enabled. Your subscription will continue past the current period.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resume subscription.");
    } finally {
      setResumeLoading(false);
    }
  }

  function handleSelectPlan(planId: PricingPlanId) {
    setError(null);
    setSuccessMessage(null);
    const isTrialEligible = planId === "starter" && !sub.hasUsedTrial && tier === "free";

    if (isActive) {
      const currentIdx = PLAN_ORDER.indexOf(tier);
      const targetIdx = PLAN_ORDER.indexOf(planId);
      setPendingPlanChange({
        planId,
        isUpgrade: targetIdx > currentIdx,
        isDowngrade: targetIdx < currentIdx,
      });
    } else {
      startCheckout(planId, { withTrial: isTrialEligible });
    }
  }

  async function handleConfirmPlanChange() {
    if (!pendingPlanChange) return;
    const { planId } = pendingPlanChange;
    setPendingPlanChange(null);

    await startCheckout(planId, {
      onPlanUpdated: () => {
        const planDef = PRICING_PLANS.find((p) => p.id === planId);
        setSuccessMessage(`Plan updated to ${planDef?.name ?? planId}. Changes are live immediately.`);
      },
    });
  }

  function getConfirmDialogProps() {
    if (!pendingPlanChange) return { title: "", description: "", confirmText: "" };
    const planDef = PRICING_PLANS.find((p) => p.id === pendingPlanChange.planId);
    const planName = planDef?.name ?? pendingPlanChange.planId;
    if (pendingPlanChange.isUpgrade) {
      return {
        title: `Upgrade to ${planName}?`,
        description: `Your plan switches to ${planName} ($${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo) immediately. A new billing cycle starts now.`,
        confirmText: `Upgrade to ${planName}`,
      };
    }
    if (pendingPlanChange.isDowngrade) {
      return {
        title: `Downgrade to ${planName}?`,
        description: `Your plan switches to ${planName} ($${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo) immediately with prorated credit. Some features may become unavailable.`,
        confirmText: `Downgrade to ${planName}`,
      };
    }
    return {
      title: `Switch to ${planName}?`,
      description: `Your plan will be switched to ${planName} immediately.`,
      confirmText: `Switch to ${planName}`,
    };
  }

  const dialogProps = getConfirmDialogProps();

  return (
    <div className="mx-auto max-w-3xl space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Subscription</h1>
          <p className="text-sm text-muted-foreground">
            Manage your BlockyNetworks subscription and billing
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:underline">
            Dashboard
          </Link>
        </div>
      </div>

      {/* ── Banners ── */}

      {/* Grace period */}
      {sub.gracePeriod.isInGracePeriod && sub.gracePeriod.graceEndsAt && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="font-semibold text-orange-800 dark:text-orange-200">
                Grace period — {daysUntil(sub.gracePeriod.graceEndsAt)} day{daysUntil(sub.gracePeriod.graceEndsAt) !== 1 ? "s" : ""} left
              </p>
              <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                Your subscription ended but you still have access until{" "}
                <span className="font-medium">{formatDate(sub.gracePeriod.graceEndsAt)}</span>.
                Re-subscribe below to prevent your organizations from being archived.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trial */}
      {sub.isTrialing && sub.trialEndsAt && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              Free Trial
            </span>
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {daysUntil(sub.trialEndsAt)} day{daysUntil(sub.trialEndsAt) !== 1 ? "s" : ""} remaining
            </span>
          </div>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            Your {TRIAL_DAYS}-day free trial ends on{" "}
            <span className="font-medium">{formatDate(sub.trialEndsAt)}</span>.
            Your saved payment method will be charged automatically if you don&apos;t cancel.
          </p>
        </div>
      )}

      {/* Pending cancellation */}
      {isCancelScheduled && sub.currentPeriodEnd && !sub.gracePeriod.isInGracePeriod && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Cancellation scheduled</p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Your access continues until{" "}
                <span className="font-medium">{billingDate}</span>, then you&apos;ll move to the free plan.
              </p>
            </div>
            <Button
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
              onClick={handleResume}
              disabled={resumeLoading}
            >
              {resumeLoading ? "Resuming..." : "Resume Subscription"}
            </Button>
          </div>
        </div>
      )}

      {/* Past due */}
      {status === "past_due" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="font-medium text-red-800 dark:text-red-200">Payment failed</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            Your last payment did not go through. Update your payment method via the customer portal to avoid losing access.
          </p>
          <Button
            className="mt-3"
            variant="outline"
            onClick={handleOpenPortal}
            disabled={portalLoading}
          >
            {portalLoading ? "Opening..." : "Update Payment Method"}
          </Button>
        </div>
      )}

      {/* Success */}
      {successMessage && (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {/* Error */}
      {(error || checkoutError) && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || checkoutError}
        </div>
      )}

      {/* ── Current plan card ── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Current Plan</CardTitle>
              {hasSubscription && (
                <StatusBadge status={status} cancelAtPeriodEnd={isCancelScheduled} />
              )}
            </div>
            {hasSubscription && (() => {
              const planDef = PRICING_PLANS.find((p) => p.id === tier);
              return planDef ? (
                <div className="text-right">
                  <div className="text-2xl font-bold">${planDef.priceMonthly.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">per month</div>
                </div>
              ) : null;
            })()}
          </div>
          <CardDescription>
            {hasSubscription
              ? isCancelScheduled
                ? "Your subscription is scheduled to cancel at the end of the current billing period."
                : "You have an active subscription. Cancel any time — access continues through the end of your billing period."
              : "You are on the free plan. Upgrade to unlock server management features."}
          </CardDescription>
        </CardHeader>

        {hasSubscription && (
          <CardContent className="space-y-5">
            {/* Plan details grid */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted-foreground">Plan</div>
                <div className="mt-1 text-sm font-semibold">{tierLabel(tier)}</div>
              </div>

              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted-foreground">
                  {isCancelScheduled ? "Access ends" : sub.isTrialing ? "Trial ends" : "Next renewal"}
                </div>
                <div className="mt-1 text-sm font-semibold">
                  {isCancelScheduled
                    ? (formatDate(sub.currentPeriodEnd) ?? "—")
                    : sub.isTrialing
                      ? (formatDate(sub.trialEndsAt) ?? billingDate ?? "—")
                      : (billingDate ?? "—")}
                </div>
                {isCancelScheduled && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Then downgraded to free
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted-foreground">Billing cycle</div>
                <div className="mt-1 text-sm font-semibold">Monthly</div>
              </div>

              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted-foreground">Subscribed since</div>
                <div className="mt-1 text-sm font-semibold">
                  {formatDate(summary?.startedAt) ?? "—"}
                </div>
              </div>
            </div>

            {/* Usage entitlements */}
            {serverLimit > 0 && (
              <div>
                <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Organization / Server Usage
                </p>
                <UsageBar used={sub.activeOrgCount} limit={serverLimit} />
                {sub.activeOrgCount >= serverLimit && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    You&apos;ve reached your limit. Upgrade your plan to add more.
                  </p>
                )}
              </div>
            )}

            {/* Features included */}
            {(tier as string) !== "free" && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  What&apos;s included
                </p>
                <PlanFeatures tier={tier} />
              </div>
            )}
          </CardContent>
        )}

        {!hasSubscription && (
          <CardContent>
            <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-base font-semibold text-foreground">Unlock Server Management</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {!sub.hasUsedTrial
                      ? `Start your ${TRIAL_DAYS}-day free trial today — no commitment, cancel anytime.`
                      : "Subscribe to create organizations, list servers, and access all BlockyNetworks features."}
                  </p>
                </div>
                <Button
                  className={`shrink-0 gap-2 px-6 ${!sub.hasUsedTrial ? "bg-green-600 hover:bg-green-700" : ""}`}
                  onClick={() => handleSelectPlan(!sub.hasUsedTrial ? "starter" : "plus")}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan !== null ? "Processing..." : !sub.hasUsedTrial ? "Start Free Trial" : "Upgrade Now"}
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ── Manage Subscription card ── */}
      {hasSubscription && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Manage Subscription</CardTitle>
            <CardDescription>
              View invoices, download receipts, and update your payment method via the secure Polar customer portal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className="gap-2"
              >
                {portalLoading ? (
                  "Opening..."
                ) : (
                  <>
                    Manage Subscription
                    <ExternalLinkIcon />
                  </>
                )}
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Redirects to Polar&apos;s secure billing portal. You&apos;ll be returned to this page when done.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Plan upgrade/change ── */}
      {hasSubscription && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {isActive ? "Change Plan" : "Choose a Plan"}
            </CardTitle>
            {isActive && (
              <CardDescription>
                Plan changes take effect immediately. Upgrades start a new billing cycle; downgrades receive prorated credit.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 lg:grid-cols-3">
              {PRICING_PLANS.map((plan) => {
                const isCurrent = isActive && tier === (plan.id as SubscriptionTier);
                const isTrialEligible = plan.id === "starter" && !sub.hasUsedTrial && (tier as string) === "free";
                const currentIdx = PLAN_ORDER.indexOf(tier);
                const targetIdx = PLAN_ORDER.indexOf(plan.id);
                const isUpgrade = isActive && targetIdx > currentIdx;
                const isDowngrade = isActive && targetIdx < currentIdx;

                let buttonLabel = "Subscribe";
                if (isTrialEligible) buttonLabel = `Start ${TRIAL_DAYS}-Day Free Trial`;
                else if (isUpgrade) buttonLabel = `Upgrade`;
                else if (isDowngrade) buttonLabel = `Downgrade`;
                else if (isActive) buttonLabel = "Switch Plan";
                else if (sub.gracePeriod.isInGracePeriod) buttonLabel = "Re-subscribe";

                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-lg border p-5 transition-colors ${
                      isCurrent
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    {plan.badge && !isCurrent && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                        <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
                          {plan.badge}
                        </span>
                      </div>
                    )}

                    <div className="mb-3 flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold">{plan.name}</span>
                      {isCurrent && (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                          Current
                        </span>
                      )}
                    </div>

                    <div className="mb-1 text-2xl font-bold">
                      ${plan.priceMonthly.toFixed(2)}
                      <span className="text-sm font-normal text-muted-foreground">/mo</span>
                    </div>
                    <p className="mb-4 text-xs text-muted-foreground">{plan.description}</p>

                    <ul className="mb-5 flex-1 space-y-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckIcon />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {isCurrent ? (
                      <div className="rounded-md bg-primary/10 px-4 py-2 text-center text-sm font-medium text-primary">
                        Current Plan
                      </div>
                    ) : (
                      <Button
                        className={`w-full ${isTrialEligible ? "bg-green-600 hover:bg-green-700" : ""}`}
                        variant={isDowngrade ? "outline" : "default"}
                        onClick={() => handleSelectPlan(plan.id as PricingPlanId)}
                        disabled={loadingPlan !== null}
                      >
                        {loadingPlan === plan.id ? "Processing..." : buttonLabel}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Subscription history ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Billing History</CardTitle>
          <CardDescription>
            {hasSubscription
              ? "Recent subscription events and billing activity."
              : "Your billing history will appear here once you subscribe."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history === undefined ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No billing events yet.
              {!hasSubscription && (
                <> <Link to="/pricing" className="text-primary hover:underline">Choose a plan</Link> to get started.</>
              )}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {history.map((event) => (
                <div key={event.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium">{eventLabel(event.eventType)}</p>
                    <p className="text-xs text-muted-foreground">
                      {event.tier && <span className="capitalize">{event.tier}</span>}
                      {event.tier && event.status && <span className="mx-1">·</span>}
                      {event.status && <span className="capitalize">{event.status}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {formatDate(event.processedAt)}
                    </p>
                    {event.success ? (
                      <span className="text-xs text-green-600 dark:text-green-400">Processed</span>
                    ) : (
                      <span className="text-xs text-red-500">Failed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasSubscription && (
            <div className="mt-4 border-t border-border pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className="gap-2 text-xs"
              >
                {portalLoading ? "Opening..." : "View Full History in Polar"}
                <ExternalLinkIcon />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Invoices & Receipts ── */}
      {hasSubscription && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invoices & Receipts</CardTitle>
            <CardDescription>
              Payment receipts from your subscription charges.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-3/4" />
              </div>
            ) : invoices === null || invoices.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {invoices === null
                  ? "Loading invoices..."
                  : "No invoices yet. Invoices appear here after your first payment."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <th className="pb-2 pr-4">Date</th>
                      <th className="pb-2 pr-4">Description</th>
                      <th className="pb-2 pr-4 text-right">Amount</th>
                      <th className="pb-2 pr-4 text-right">Status</th>
                      <th className="pb-2 text-right">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="group">
                        <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                          {inv.createdAt
                            ? new Date(inv.createdAt).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="py-3 pr-4">
                          <span className="font-medium">
                            {inv.productName ?? tierLabel(tier)}
                          </span>
                          {inv.billingReason && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              ({inv.billingReason === "subscription_cycle"
                                ? "Renewal"
                                : inv.billingReason === "subscription_create"
                                  ? "Initial"
                                  : inv.billingReason === "subscription_update"
                                    ? "Plan change"
                                    : inv.billingReason.replace(/_/g, " ")})
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-right whitespace-nowrap font-medium">
                          {formatCurrency(inv.amount, inv.currency)}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <InvoiceStatusBadge status={inv.status} />
                        </td>
                        <td className="py-3 text-right">
                          {inv.invoiceUrl ? (
                            <a
                              href={inv.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              View
                              <ExternalLinkIcon />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 border-t border-border pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className="gap-2 text-xs"
              >
                {portalLoading ? "Opening..." : "View All Invoices in Polar"}
                <ExternalLinkIcon />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Cancel section ── */}
      {isActive && !isCancelScheduled && (
        <div className="border-t border-border pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-medium">Cancel your subscription</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Your access continues through{" "}
                <span className="font-medium">{billingDate ?? "your billing period end"}</span>.
                No refunds for unused time.
              </p>
            </div>
            <Button
              variant="outline"
              className="shrink-0 border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:border-red-600 dark:hover:bg-red-900/20"
              onClick={() => setShowCancelModal(true)}
              disabled={cancelling}
            >
              {cancelling ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          </div>
        </div>
      )}

      {/* ── Modals ── */}

      {/* Cancellation confirmation modal with retention messaging */}
      <CancellationConfirmationModal
        open={showCancelModal}
        tier={tier}
        periodEndDate={billingDate}
        activeOrgCount={sub.activeOrgCount}
        serverLimit={serverLimit}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
      />

      {/* Plan change confirmation dialog */}
      <ConfirmDialog
        open={pendingPlanChange !== null}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText="Keep current plan"
        variant={pendingPlanChange?.isDowngrade ? "destructive" : "default"}
        onConfirm={handleConfirmPlanChange}
        onCancel={() => setPendingPlanChange(null)}
      />
    </div>
  );
}
